import * as THREE from 'three';
import { getLightingQuality } from '../engine/lightingQuality.js';

const QUALITY = getLightingQuality();

function randomRange(min, max) {
  return min + Math.random() * (max - min);
}

function resolveRoomBounds(room) {
  if (room?.getAtmosphereBounds) return room.getAtmosphereBounds();
  return { minX: -40, maxX: 40, minZ: -40, maxZ: 40 };
}

export class AtmosphereDustSystem {
  constructor(scene) {
    this.scene = scene;
    this.group = new THREE.Group();
    this.group.renderOrder = 4;
    this.group.visible = !!QUALITY.cinematic.dust.enabled;
    this.scene.add(this.group);

    this.sprites = [];
    this.bounds = { minX: -40, maxX: 40, minZ: -40, maxZ: 40 };
    this._spawnYMin = 0.45;
    this._spawnYMax = 2.8;

    this._initPool();
  }

  _initPool() {
    const count = Math.max(0, QUALITY.cinematic.dust.count | 0);
    const sharedMaterial = new THREE.SpriteMaterial({
      color: 0xff6688,
      transparent: true,
      opacity: QUALITY.cinematic.dust.opacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true,
    });
    for (let i = 0; i < count; i++) {
      const sprite = new THREE.Sprite(sharedMaterial.clone());
      const size = randomRange(0.06, 0.2);
      sprite.scale.set(size, size, 1);
      sprite.position.set(0, 0, 0);
      this.group.add(sprite);
      this.sprites.push({
        sprite,
        vx: randomRange(-0.25, 0.25),
        vz: randomRange(-0.25, 0.25),
        vy: randomRange(-0.02, 0.05),
        pulseSeed: Math.random() * Math.PI * 2,
      });
    }
  }

  rebuildForRoom(room) {
    this.bounds = resolveRoomBounds(room);
    const { minX, maxX, minZ, maxZ } = this.bounds;
    for (const p of this.sprites) {
      p.sprite.position.set(
        randomRange(minX, maxX),
        randomRange(this._spawnYMin, this._spawnYMax),
        randomRange(minZ, maxZ),
      );
    }
  }

  update(dt, player) {
    if (!this.group.visible) return;
    const { minX, maxX, minZ, maxZ } = this.bounds;
    const px = player?.x ?? 0;
    const pz = player?.z ?? 0;
    const pulseT = performance.now() * 0.001;
    for (const p of this.sprites) {
      const s = p.sprite;
      s.position.x += p.vx * dt;
      s.position.z += p.vz * dt;
      s.position.y += p.vy * dt;

      if (s.position.x < minX) s.position.x = maxX;
      else if (s.position.x > maxX) s.position.x = minX;
      if (s.position.z < minZ) s.position.z = maxZ;
      else if (s.position.z > maxZ) s.position.z = minZ;
      if (s.position.y < this._spawnYMin) s.position.y = this._spawnYMax;
      else if (s.position.y > this._spawnYMax) s.position.y = this._spawnYMin;

      const d = Math.hypot(s.position.x - px, s.position.z - pz);
      const nearPlayer = THREE.MathUtils.clamp(1 - d / 13, 0, 1);
      const pulse = 0.88 + Math.sin(pulseT * 1.4 + p.pulseSeed) * 0.12;
      s.material.opacity = QUALITY.cinematic.dust.opacity * (0.75 + nearPlayer * 0.65) * pulse;
    }
  }

  dispose() {
    this.scene.remove(this.group);
    this.group.traverse((obj) => {
      if (obj.material) obj.material.dispose?.();
    });
    this.sprites.length = 0;
  }
}

